import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle } from '../Card';
import { ReactComponent as PatientIcon } from '../../assets/icons/patient.svg';
import { FormField } from '../form/FormField';
import { ToggleButtons } from '../ToggleButtons';
import { ToggleButton } from '../ToggleButton';
import { CheckMark } from '../CheckMark';
import { Button } from '../buttons/Button';
import { ArrowForward } from '@material-ui/icons';
import { ReactComponent as DoctorIcon } from '../../assets/icons/doctor.svg';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { selectRequestType } from '../../store/auth/auth.selector';
import { authActions } from '../../store/auth/auth.reducer';
import { Colors } from '../../constants/colors';
import { Header } from '../Header';
import { Container } from '../helpers/Container';
import { AlignContent } from '../helpers/AlignContent';
import { defaultRequestType, RequestType } from '../../model/enums/RequestType';
import { useMountEffect } from '../../hooks/useMountEffect';
import { CenteredButton } from '../buttons/CenteredButton';
import { Error } from '../Error';
import { useNavigate } from 'react-router-dom';
import { getShowPatientSection } from '../../hooks/getShowPatientSection';
import { getShowSiteUnderConstruction } from '../../hooks/getShowSiteUnderConstruction';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardsHolder: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        marginTop: 30
      },
      '& > div': {
        margin: '0 30px',
        flexBasis: 'calc(50% - 60px)',
        width: 'calc(50% - 60px)',
        [theme.breakpoints.down('md')]: {
          margin: '0 15px'
        },
        [theme.breakpoints.down('sm')]: {
          margin: '0 0 30px',
          '&:last-child': {
            marginBottom: 0
          }
        }
      }
    },
    paragraph: {
      margin: '0 0 30px',
      color: Colors.dark
    },
    subheader: {
      display: 'block',
      marginBottom: '10px'
    }
  })
);

export const Login = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const requestType = useSelector(selectRequestType);
  const firstCardActive = requestType === RequestType.PatientRegistration || requestType === RequestType.ReceiptOrder;
  const secondCartActive = requestType === RequestType.DoctorRegistration;

  const [lastPatientRequestType, setLastPatientRequestType] = useState<RequestType>(defaultRequestType);
  const showPatientSection: boolean = getShowPatientSection();

  console.log('showPatientSection', showPatientSection);

  useMountEffect(() => {
    dispatch(authActions.resetState());
  });

  useEffect(() => {
    if (requestType === RequestType.PatientRegistration || requestType === RequestType.ReceiptOrder) {
      setLastPatientRequestType(requestType);
    }
  }, [requestType]);

  const handleFirstCardClick = () => {
    if (secondCartActive) {
      // Set to previously clicked patient request type
      dispatch(authActions.setRequestType(lastPatientRequestType));
    } else {
      // Toggle between patient request types
      dispatch(authActions.setRequestType(requestType === RequestType.PatientRegistration ? RequestType.ReceiptOrder : RequestType.PatientRegistration));
    }
  };

  const handleContinueClick = () => {
    navigate('/verification');
  };

  return (<>
    <Header />
    <Container>
      <AlignContent to="center" expand={true}>
        <div className={classes.cardsHolder}>
          {showPatientSection &&
            <Card active={firstCardActive}
                  onClick={handleFirstCardClick}
            >

              <CardTitle active={firstCardActive}>
                <PatientIcon />
                <span>Jsem <strong>pacient</strong></span>
              </CardTitle>

              <CardBody>
                <FormField>
                  <ToggleButtons>
                    <ToggleButton active={lastPatientRequestType === RequestType.ReceiptOrder}
                                  disabled={!firstCardActive}
                                  onClick={() => dispatch(authActions.setRequestType(RequestType.ReceiptOrder))}
                                  type="button">
                      <CheckMark active={lastPatientRequestType === RequestType.ReceiptOrder} />
                      <span>Žádám o vystavení eReceptu</span>
                    </ToggleButton>
                    <ToggleButton active={lastPatientRequestType === RequestType.PatientRegistration}
                                  disabled={!firstCardActive}
                                  onClick={() => dispatch(authActions.setRequestType(RequestType.PatientRegistration))}
                                  type="button">
                      <CheckMark active={lastPatientRequestType === RequestType.PatientRegistration} />
                      <span>Chci se stát pacientem IKEM</span>
                    </ToggleButton>
                  </ToggleButtons>
                </FormField>
              </CardBody>

              <p className={classes.paragraph}>
                {(() => {
                  if (requestType === RequestType.PatientRegistration) {
                    return 'Pacienti se mohou přihlásit k péči na některé z klinik IKEM na základě doporučení ošetřujícího lékaře.';
                  } else {
                    return 'O zaslání eReceptu mohou požádat pacienti, kteří mají podepsaný "Souhlas s elektronickou komunikací".';
                  }
                })()}
              </p>
            </Card>
          }
          <Card active={secondCartActive}
                onClick={() => dispatch(authActions.setRequestType(RequestType.DoctorRegistration))}>

            <CardTitle active={secondCartActive}>
              <DoctorIcon />
              <span>Jsem <strong>lékař</strong></span>
            </CardTitle>

            <CardBody>
              <p className={classes.paragraph}>
                Lékaři mají možnost objednat svého pacienta na některou z klinik IKEM do ambulantní i lůžkové péče.
              </p>
            </CardBody>
          </Card>
        </div>
        <br />
        {getShowSiteUnderConstruction() &&
          <Error errorMessage="Tato aplikace je ve fázi vývoje a není zatím připravena pro použití." />
        }
        <CenteredButton>
          <Button onClick={handleContinueClick}>
            {(() => {
              if (requestType === RequestType.ReceiptOrder) {
                return 'Pokračovat k objednání receptu pro pacienta';
              } else if (requestType === RequestType.DoctorRegistration) {
                return 'Pokračovat jako lékař';
              } else {
                return 'Pokračovat k objednání nového pacienta IKEM';
              }
            })()}
            <ArrowForward />
          </Button>
        </CenteredButton>
      </AlignContent>
    </Container>
  </>);
};
